import { Show, createEffect, createSignal } from 'solid-js'

import { dispatchAccountAction } from '../../runtimeBridge'
import { useI18n } from '../../i18n'
import {
   closeExistingSyncKeyModal,
   isExistingSyncKeyModalOpen,
} from '../../state/appUiStore'

/**
 * Renders the modal heading copy.
 *
 * @returns The heading element.
 */
const ModalHeading = () => {
   const t = useI18n()
   return (
      <div class="space-y-2">
         <p class="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-300/80">
            {t('Cloud Sync')}
         </p>
         <h2 class="text-2xl font-semibold text-ink">
            {t('Use an existing synchronization key')}
         </h2>
         <p class="text-sm leading-6 text-ink-secondary">
            {t(
               'Enter the synchronization key from another device to load the cloud data connected to it on this device.',
            )}
         </p>
      </div>
   )
}

/**
 * Renders the cancel/submit button row.
 *
 * @param props - The cancel handler.
 * @returns The buttons element.
 */
const ModalActions = (props: { onCancel: () => void }) => {
   const t = useI18n()
   return (
      <div class="flex flex-col gap-3 sm:flex-row sm:justify-end">
         <button
            type="button"
            onClick={props.onCancel}
            class="rounded-2xl border border-edge bg-surface-raised px-4 py-3 text-sm font-semibold text-ink transition hover:bg-surface-control focus:outline-none focus:ring-2 focus:ring-ink-muted focus:ring-offset-2 focus:ring-offset-surface"
         >
            {t('Cancel')}
         </button>
         <button
            type="submit"
            class="rounded-2xl bg-emerald-500 px-4 py-3 text-sm font-semibold text-ink-inverse transition hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2 focus:ring-offset-surface"
         >
            {t('Load sync key')}
         </button>
      </div>
   )
}

/**
 * Renders the modal for entering an existing sync key.
 *
 * @returns The modal element.
 */
const ExistingSyncKeyModal = () => {
   const t = useI18n()
   const [syncKey, setSyncKey] = createSignal('')
   const [errorMessage, setErrorMessage] = createSignal('')
   let inputRef: HTMLInputElement | undefined

   createEffect(() => {
      if (!isExistingSyncKeyModalOpen()) {
         setSyncKey('')
         setErrorMessage('')
         return
      }
      queueMicrotask(() => {
         inputRef?.focus()
         inputRef?.select()
      })
   })

   /**
    * Submits the entered sync key.
    *
    * @param event - The form submit event.
    */
   const handleSubmit = (event: SubmitEvent) => {
      event.preventDefault()
      const trimmedSyncKey = syncKey().trim()
      if (!trimmedSyncKey) {
         setErrorMessage(t('Enter a valid synchronization key.'))
         return
      }
      dispatchAccountAction({ action: 'enter-sync-key', syncKey: trimmedSyncKey })
      closeExistingSyncKeyModal()
   }

   return (
      <Show when={isExistingSyncKeyModalOpen()}>
         <div
            class="fixed inset-0 z-50 flex items-center justify-center bg-surface-sunken/80 p-4 backdrop-blur-sm"
            role="presentation"
            onClick={event => {
               if (event.target === event.currentTarget) {
                  closeExistingSyncKeyModal()
               }
            }}
            onKeyDown={event => {
               if (event.key === 'Escape') {
                  closeExistingSyncKeyModal()
               }
            }}
         >
            <form
               class="w-full max-w-md space-y-5 rounded-3xl border border-edge-soft bg-surface/95 p-6 shadow-2xl shadow-surface-sunken/40"
               onSubmit={handleSubmit}
            >
               <ModalHeading />
               <div class="space-y-2">
                  <label
                     for="existing-sync-key-input"
                     class="block text-sm font-medium text-ink-soft"
                  >
                     {t('Synchronization Key')}
                  </label>
                  <input
                     ref={inputRef}
                     id="existing-sync-key-input"
                     type="text"
                     value={syncKey()}
                     onInput={event => {
                        setSyncKey(event.currentTarget.value)
                        setErrorMessage('')
                     }}
                     placeholder={t('Paste the synchronization key')}
                     autocomplete="off"
                     spellcheck={false}
                     class="w-full rounded-2xl border border-edge bg-surface-raised px-4 py-3 text-sm text-ink outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30"
                  />
                  <p class="text-xs leading-5 text-ink-muted">
                     {t(
                        'If you already have local data here, it will be replaced with the synced data tied to this key.',
                     )}
                  </p>
                  <Show when={errorMessage()}>
                     <p class="text-sm text-rose-300" aria-live="polite">
                        {errorMessage()}
                     </p>
                  </Show>
               </div>
               <ModalActions onCancel={closeExistingSyncKeyModal} />
            </form>
         </div>
      </Show>
   )
}

export default ExistingSyncKeyModal
